import {
  Component,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Typography, Button, Space } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";

import { TradeConfirmationRow, BondDistributionFile } from "./types";
import { Part1 } from "./Part1";
import { Part2 } from "./Part2";
import { loadRefundDistributionFilesFromBackend } from "./distributionStorage";

const { Title, Text } = Typography;
const REFUND_DISTRIBUTION_FILES_STORAGE_KEY =
  "refund-bond-distribution-files-v4";
const REFUND_DISTRIBUTION_DB_NAME =
  "refund-bond-distribution-db-v4";
const REFUND_DISTRIBUTION_DB_STORE =
  "bond-distribution-data";
const REFUND_DISTRIBUTION_DB_RECORD_KEY =
  "current";

const isPlainObject = (
  value: unknown
): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value);

const loadStoredBondDistributionFiles = (): UploadFile[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(
      REFUND_DISTRIBUTION_FILES_STORAGE_KEY
    );
    if (!raw) return [];
    const stored = JSON.parse(raw);
    if (!Array.isArray(stored)) return [];

    return stored
      .filter(isPlainObject)
      .map((file) => ({
        uid:
          typeof file.uid === "string"
            ? file.uid
            : `bond-distribution:${Math.random()}`,
        name:
          typeof file.name === "string"
            ? file.name
            : "Тархалтын файл",
        status:
          typeof file.status === "string"
            ? file.status
            : "done",
      })) as UploadFile[];
  } catch {
    return [];
  }
};

const openRefundDistributionDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(
      REFUND_DISTRIBUTION_DB_NAME,
      1
    );
    request.onupgradeneeded = () => {
      const db = request.result;
      if (
        !db.objectStoreNames.contains(
          REFUND_DISTRIBUTION_DB_STORE
        )
      ) {
        db.createObjectStore(
          REFUND_DISTRIBUTION_DB_STORE
        );
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const loadBondDistributionDataFromIndexedDb =
  async (): Promise<BondDistributionFile[]> => {
    if (typeof window === "undefined" || !window.indexedDB)
      return [];
    try {
      const db = await openRefundDistributionDb();
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(
          REFUND_DISTRIBUTION_DB_STORE,
          "readonly"
        );
        const store = tx.objectStore(
          REFUND_DISTRIBUTION_DB_STORE
        );
        const request = store.get(
          REFUND_DISTRIBUTION_DB_RECORD_KEY
        );
        request.onsuccess = () => {
          const result = request.result;
          resolve(
            Array.isArray(result)
              ? (result as BondDistributionFile[])
              : []
          );
        };
        request.onerror = () => reject(request.error);
      });
    } catch {
      return [];
    }
  };

const saveBondDistributionDataToIndexedDb = async (
  data: BondDistributionFile[]
) => {
  if (typeof window === "undefined" || !window.indexedDB)
    return;
  const db = await openRefundDistributionDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(
      REFUND_DISTRIBUTION_DB_STORE,
      "readwrite"
    );
    const store = tx.objectStore(
      REFUND_DISTRIBUTION_DB_STORE
    );
    store.put(data, REFUND_DISTRIBUTION_DB_RECORD_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};

const mergeByKey = <T,>(
  items: T[],
  getKey: (item: T) => string
): T[] => {
  const map = new Map<string, T>();
  items.forEach((item) => {
    map.set(getKey(item), item);
  });
  return Array.from(map.values());
};

const resetBondDistributionStorage = async () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(
      REFUND_DISTRIBUTION_FILES_STORAGE_KEY
    );
  } catch {}
  try {
    const request = window.indexedDB.deleteDatabase(
      REFUND_DISTRIBUTION_DB_NAME
    );
    await new Promise<void>((resolve) => {
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
    });
  } catch {}
};

class RefundErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error(
      "RefundCalculationWidget render error",
      error
    );
  }

  handleReset = async () => {
    await resetBondDistributionStorage();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Space
          direction="vertical"
          size="middle"
          style={{ width: "100%" }}
        >
          <Title level={3}>
            Буцаалтын тооцоолол
          </Title>
          <Text type="danger">
            Refund page render хийх үед алдаа гарлаа.
          </Text>
          <Text type="secondary">
            Хадгалсан тархалтын file state эвдэрсэн бол
            доорх товчоор цэвэрлээд дахин ачаалж болно.
          </Text>
          <Button onClick={this.handleReset}>
            Тархалтын cache цэвэрлээд дахин ачаалах
          </Button>
        </Space>
      );
    }

    return this.props.children;
  }
}

export const RefundCalculationWidget = () => {
  const [currentPart, setCurrentPart] = useState<1 | 2>(1);
  const [showDistributionManager, setShowDistributionManager] = useState(false);
  const [combinedTC, setCombinedTC] = useState<TradeConfirmationRow[]>([]);
  const [bondDistributionFiles, setBondDistributionFiles] =
    useState<UploadFile[]>(() =>
      loadStoredBondDistributionFiles()
    );
  const [bondDistributionData, setBondDistributionData] = useState<BondDistributionFile[]>(
    []
  );
  const [
    hasHydratedBondDistributionData,
    setHasHydratedBondDistributionData,
  ] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        REFUND_DISTRIBUTION_FILES_STORAGE_KEY,
        JSON.stringify(
          bondDistributionFiles.map((file) => ({
            uid: file.uid,
            name: file.name,
            status: file.status,
          }))
        )
      );
    } catch (error) {
      console.warn(
        "Failed to persist refund distribution file metadata",
        error
      );
    }
  }, [bondDistributionFiles]);

  useEffect(() => {
    if (!hasHydratedBondDistributionData) return;
    void saveBondDistributionDataToIndexedDb(
      bondDistributionData
    );
  }, [
    bondDistributionData,
    hasHydratedBondDistributionData,
  ]);

  useEffect(() => {
    let mounted = true;
    void Promise.all([
      loadRefundDistributionFilesFromBackend(),
      loadBondDistributionDataFromIndexedDb(),
    ]).then(([backendState, localData]) => {
        if (mounted) {
          setBondDistributionFiles((prev) =>
            mergeByKey(
              [
                ...prev,
                ...backendState.files,
              ],
              (file) => String(file.uid || "")
            )
          );
          setBondDistributionData(
            mergeByKey(
              [
                ...localData,
                ...backendState.data,
              ],
              (file) =>
                `${file.bondName}|${
                  file.uploadSlotId || "quarter-1"
                }`
            )
          );
          setHasHydratedBondDistributionData(true);
        }
      }
    );
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <RefundErrorBoundary>
      <div
        style={{
          padding: "32px 24px 64px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <div style={{ marginBottom: 8 }}>
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Step {currentPart} of 2
              </Text>
            </div>
            <Title
              level={2}
              style={{ margin: 0, fontWeight: 600 }}
            >
              Буцаалтын тооцоолол
            </Title>
          </div>
          {currentPart === 1 && (
            <Button
              icon={
                showDistributionManager ? (
                  <EyeInvisibleOutlined />
                ) : (
                  <EyeOutlined />
                )
              }
              onClick={() =>
                setShowDistributionManager(
                  !showDistributionManager
                )
              }
              style={{ marginTop: 24 }}
            >
              Файл удирдлага
            </Button>
          )}
        </div>

        <div style={{ height: 32 }} />

        {currentPart === 1 ? (
          <Part1
            combinedTC={combinedTC}
            setCombinedTC={setCombinedTC}
            bondDistributionFiles={bondDistributionFiles}
            setBondDistributionFiles={setBondDistributionFiles}
            bondDistributionData={bondDistributionData}
            setBondDistributionData={setBondDistributionData}
            onContinue={() => setCurrentPart(2)}
            showDistributionManager={showDistributionManager}
          />
        ) : (
          <Part2
            combinedTC={combinedTC}
            bondDistributionData={bondDistributionData}
            onBack={() => setCurrentPart(1)}
          />
        )}
      </div>
    </RefundErrorBoundary>
  );
};
